import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./style.css";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { IoMdClose } from "react-icons/io";
import { HiOutlineCheckCircle } from "react-icons/hi2";
import WashtaLogo from "./washta-logo.png";
import { CiMail } from "react-icons/ci";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import { FaRegFileAlt } from "react-icons/fa";
import WashtaHero from "components/washta-hero-section/WashtaHero";
import Nav from "../PhoneResponsiveHeader/Nav";

// ── Modal copy config ──────────────────────────────────────────────
const MODAL_COPY = {
  building: {
    title: "Request a residential wash in your building",
    sub: "Tell us where you park at home. When we launch recurring washes in your area, you'll be first to know.",
    showBuilding: true,
    success:
      "Thanks! We've noted your building and will reach out as soon as recurring washes go live in your area.",
  },
  packs: {
    title: "Get notified about wash packs",
    sub: "Prepaid wash packs are launching soon. Leave your details and we'll let you know the moment they're available.",
    showBuilding: false,
    success: "Thanks! We'll get in touch the moment wash packs go live.",
  },
};

// ── API submit function ────────────────────────────────────────────
async function submitRequest(kind, formData) {
  const BASE = "https://backend.washta.com/api/inquiry";

  const isBuilding = kind === "building";
  const endpoint = isBuilding ? `${BASE}/createInquiry` : BASE;

  const payload = isBuilding
    ? {
        fullName: formData.name,
        mobileNumber: formData.phone,
        email: formData.email,
        buildingCommunity: formData.building,
        area: formData.area,
      }
    : {
        fullName: formData.name,
        mobileNumber: formData.phone,
        email: formData.email,
        area: formData.area,
      };

  const response = await fetch(endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Server error: ${response.status}`);
  }
  return response.json();
}

// ── Request Modal Component ────────────────────────────────────────
function RequestModal({ open, kind, onClose }) {
  const copy = MODAL_COPY[kind] || MODAL_COPY.building;
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    building: "",
    area: "",
  });

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      setLoading(false);
      setError("");
      setForm({ name: "", phone: "", email: "", building: "", area: "" });
    }
  }, [open, kind]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await submitRequest(kind, form);
      setSubmitted(true);
    } catch (err) {
      // Even if API fails, show success to user (optional: change to setError)
      console.error("API error:", err);
      setSubmitted(true); // Remove this line if you want to show error instead
      // setError("Kuch masla aa gaya. Dobara try karein.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "24px",
          padding: "0",
          overflow: "hidden",
          boxShadow: "0 40px 90px -30px rgba(26,23,38,.5)",
          fontFamily: "'DM Sans', sans-serif",
        },
      }}
      BackdropProps={{
        sx: { backdropFilter: "blur(4px)", backgroundColor: "rgba(26,23,38,.55)" },
      }}
    >
      <DialogContent sx={{ padding: "38px 38px 34px", position: "relative" }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "#F6F7F9",
            color: "#56516A",
            "&:hover": { background: "#ECEEF1", color: "#1A1726" },
          }}
        >
          <IoMdClose fontSize="small" />
        </IconButton>

        {!submitted ? (
          /* ── Form ── */
          <form onSubmit={handleSubmit}>
            <span className="soon-badge" style={{ marginBottom: 0 }}>
              <span className="dot" style={{ background: "#6B5FE4", animation: "none" }}></span>
              Coming soon
            </span>

            <h3 className="modal-title">{copy.title}</h3>
            <p className="modal-sub">{copy.sub}</p>

            <div className="mfield">
              <label htmlFor="mui-name">Full name</label>
              <input
                id="mui-name"
                name="name"
                type="text"
                placeholder="e.g. Sara Ahmed"
                required
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div className="mrow">
              <div className="mfield">
                <label htmlFor="mui-phone">Mobile number</label>
                <input
                  id="mui-phone"
                  name="phone"
                  type="tel"
                  placeholder="+971 50 000 0000"
                  required
                  value={form.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="mfield">
                <label htmlFor="mui-email">Email</label>
                <input
                  id="mui-email"
                  name="email"
                  type="email"
                  placeholder="you@email.com"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {copy.showBuilding && (
              <div className="mfield">
                <label htmlFor="mui-building">Building / community</label>
                <input
                  id="mui-building"
                  name="building"
                  type="text"
                  placeholder="e.g. Marina Gate 1, Dubai Marina"
                  value={form.building}
                  onChange={handleChange}
                />
              </div>
            )}

            <div className="mfield">
              <label htmlFor="mui-area">Area</label>
              <select
                id="mui-area"
                name="area"
                value={form.area}
                onChange={handleChange}
              >
                <option value="">Select your area</option>
                <option>Dubai Marina</option>
                <option>Downtown Dubai</option>
                <option>Business Bay</option>
                <option>JVC / JVT</option>
                <option>Palm Jumeirah</option>
                <option>Abu Dhabi — Corniche</option>
                <option>Abu Dhabi — Reem Island</option>
                <option>Other</option>
              </select>
            </div>

            {error && (
              <p style={{ color: "#E53E3E", fontSize: "13px", marginTop: "10px" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary modal-submit"
              disabled={loading}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}
            >
              {loading ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  Submitting…
                </>
              ) : (
                "Submit request"
              )}
            </button>

            <p className="modal-fine">We'll only contact you about this service. No spam.</p>
          </form>
        ) : (
          /* ── Success state ── */
          <div className="modal-success">
            <div className="ms-check">
              <HiOutlineCheckCircle sx={{ fontSize: 32, color: "#1FB774" }} />
            </div>
            <h3 className="modal-title">Request received</h3>
            <p className="modal-sub">{copy.success}</p>
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
              style={{ width: "100%", justifyContent: "center", marginTop: "24px" }}
            >
              Done
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page Component ────────────────────────────────────────────
export default function Home1Page() {
  const pageRef = useRef(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalKind, setModalKind] = useState("building");

  const openModal = (kind) => {
    setModalKind(kind);
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const root = pageRef.current;
    if (!root) return undefined;

    document.documentElement.classList.add("anim");

    const nav = root.querySelector("#nav");
    const onScroll = () => {
      if (nav) nav.classList.toggle("scrolled", window.scrollY > 10);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();

    let revealObserver;
    if ("IntersectionObserver" in window) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("in");
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" }
      );
      root.querySelectorAll(".reveal").forEach((el) => revealObserver.observe(el));
    } else {
      root.querySelectorAll(".reveal").forEach((el) => el.classList.add("in"));
    }
    root.querySelectorAll(".hero .reveal").forEach((el) => el.classList.add("in"));

    const timers = new Set();
    let cancelled = false;
    const wait = (ms) =>
      new Promise((resolve) => {
        const id = window.setTimeout(() => {
          timers.delete(id);
          resolve();
        }, ms);
        timers.add(id);
      });
    const setTimer = (fn, ms) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
      return id;
    };

    const phone = root.querySelector(".phone-stage .phone");
    if (phone) {
      const panels = Array.from(phone.querySelectorAll(".fs"));
      const dots = Array.from(phone.querySelectorAll(".pf-stepper i"));
      const homeCta = phone.querySelector("#homeCta");
      const svcCta = phone.querySelector("#svcCta");
      const addon = phone.querySelector("#addonFresh");
      const addonCheck = addon ? addon.querySelector(".pf-check") : null;
      const notif = phone.querySelector("#pfNotif");
      const locScroll = phone.querySelector("#locScroll");
      const locSel = phone.querySelector(".loc-item.sel");
      const svcExt = phone.querySelector("#svcExterior");
      const payCta = phone.querySelector("#payCta");
      const launch = phone.querySelector("#appLaunch");
      const waIcon = phone.querySelector("#washtaIcon");
      const screenEl = phone.querySelector(".screen");

      const show = (index) => {
        panels.forEach((panel, panelIndex) => {
          panel.classList.remove("active", "past", "future");
          panel.classList.add(
            panelIndex === index ? "active" : panelIndex < index ? "past" : "future"
          );
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle("on", dotIndex === index));
      };

      const tap = (el) => {
        if (!el) return;
        el.classList.add("tapped");
        setTimer(() => el.classList.remove("tapped"), 560);
      };

      const reduceMotion =
        window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (panels.length >= 7 && !reduceMotion) {
        const loop = async () => {
          while (!cancelled) {
            if (addonCheck) addonCheck.classList.remove("on");
            if (svcCta) svcCta.textContent = "Continue · AED 20";
            if (notif) notif.classList.remove("in");
            if (svcExt) svcExt.classList.remove("sel", "pulse");
            if (locScroll) locScroll.scrollTop = 0;
            if (locSel) locSel.classList.remove("pulse");
            if (waIcon) waIcon.classList.remove("pressed");
            if (launch) {
              launch.classList.remove("opening", "done");
              launch.style.transform = "";
              launch.style.opacity = "";
            }

            show(0);
            await wait(1500);
            if (cancelled) break;
            if (waIcon) {
              waIcon.classList.add("pressed");
              await wait(220);
              waIcon.classList.remove("pressed");
              await wait(110);
            }
            if (cancelled) break;
            if (launch && waIcon && screenEl) {
              const iconRect = waIcon.getBoundingClientRect();
              const screenRect = screenEl.getBoundingClientRect();
              launch.style.setProperty(
                "--ox",
                `${(((iconRect.left + iconRect.width / 2 - screenRect.left) / screenRect.width) * 100).toFixed(1)}%`
              );
              launch.style.setProperty(
                "--oy",
                `${(((iconRect.top + iconRect.height / 2 - screenRect.top) / screenRect.height) * 100).toFixed(1)}%`
              );
              launch.classList.add("opening");
              await wait(950);
            }

            show(1);
            await wait(100);
            if (launch) launch.classList.add("done");
            await wait(650);
            if (locScroll) {
              locScroll.scrollTo({ top: locScroll.scrollHeight, behavior: "smooth" });
              await wait(1200);
              locScroll.scrollTo({ top: 0, behavior: "smooth" });
              await wait(900);
            }
            if (locSel) {
              void locSel.offsetWidth;
              locSel.classList.add("pulse");
            }
            await wait(900);

            show(2);
            await wait(1500);
            if (svcExt) {
              svcExt.classList.add("sel");
              void svcExt.offsetWidth;
              svcExt.classList.add("pulse");
            }
            await wait(1100);
            tap(homeCta);
            await wait(680);

            show(3);
            await wait(1800);
            if (addonCheck) addonCheck.classList.add("on");
            if (svcCta) svcCta.textContent = "Continue · AED 25";
            await wait(1500);
            tap(svcCta);
            await wait(680);

            show(4);
            await wait(2600);
            tap(payCta);
            await wait(760);

            show(5);
            await wait(3000);

            show(6);
            await wait(650);
            if (notif) notif.classList.add("in");
            await wait(3600);
          }
        };
        loop();
      } else if (panels.length) {
        show(0);
      }
    }

    let painted = false;
    let rafTwo = null;
    const rafOne = window.requestAnimationFrame(() => {
      rafTwo = window.requestAnimationFrame(() => {
        painted = true;
      });
    });
    const watchdog = window.setTimeout(() => {
      if (!painted) document.documentElement.classList.remove("anim");
    }, 280);

    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
      window.clearTimeout(watchdog);
      window.cancelAnimationFrame(rafOne);
      if (rafTwo) window.cancelAnimationFrame(rafTwo);
      window.removeEventListener("scroll", onScroll);
      if (revealObserver) revealObserver.disconnect();
      document.body.style.overflow = "";
      document.documentElement.classList.remove("anim");
    };
  }, []);
const scrollToApply = (e) => {
    e.preventDefault();
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <>
      <Helmet>
        <title>Washta — Park. Shop. Drive away clean.</title>
        <meta
          name="description"
          content="Book a carwash while you are parked at the mall. Washta connects you with stationed car care providers in Dubai and Abu Dhabi."
        />
      </Helmet>

      {/* ── MUI Modal (replaces old vanilla JS modal) ── */}
      <RequestModal open={modalOpen} kind={modalKind} onClose={closeModal} />

      <main ref={pageRef} className="washta-landing-page">
        <header className="nav-version2" id="nav">
          <div className="wrap nav-inner">
            <a href="#" className="logo" aria-label="Washta">
              <img className="wordmark" src={WashtaLogo} alt="Washta" />
            </a>

            <nav className="nav-links">
              <a href="#how">How it works</a>
              <a href="/for-providers">For providers</a>
            </nav>

            <div className="nav-cta">
              <a
                href="https://apps.apple.com/us/app/washta/id6693265373"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge"
                aria-label="Download on the App Store"
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.564 13.02c-.025-2.55 2.083-3.77 2.176-3.83-1.186-1.74-3.03-1.97-3.687-2-1.57-.16-3.06.92-3.86.92-.79 0-2.018-.9-3.32-.875-1.71.025-3.29 1-4.17 2.53-1.78 3.08-.456 7.64 1.276 10.14.847 1.22 1.857 2.6 3.18 2.55 1.276-.05 1.758-.825 3.3-.825 1.54 0 1.974.825 3.32.8 1.37-.025 2.24-1.25 3.08-2.48.97-1.42 1.37-2.8 1.39-2.87-.03-.013-2.668-1.025-2.695-4.06zM15.06 5.78c.7-.85 1.174-2.03 1.044-3.21-1.01.04-2.23.673-2.954 1.52-.65.75-1.22 1.95-1.067 3.1 1.127.088 2.277-.573 2.977-1.41z"></path>
                </svg>
                <span className="sb-txt">
                  <small>Download on the</small>
                  <b>App Store</b>
                </span>
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.washta"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge"
                aria-label="Get it on Google Play"
              >
                <svg viewBox="0 0 24 24">
                  <path fill="#34D399" d="M3.6 2.4c-.2.2-.3.5-.3.9v17.4c0 .4.1.7.3.9l9.2-9.6z"></path>
                  <path fill="#60A5FA" d="M16.8 8.3 12.8 12l4 3.7 3.1-1.8c.9-.5.9-1.9 0-2.4z"></path>
                  <path fill="#FBBF24" d="m12.8 12-9.2 9.6c.3.3.8.4 1.3.1l12-6.9z"></path>
                  <path fill="#F87171" d="M3.6 2.4 12.8 12l4-3.7-12-6.9c-.5-.3-1-.2-1.2.1z"></path>
                </svg>
                <span className="sb-txt">
                  <small>Get it on</small>
                  <b>Google Play</b>
                </span>
              </a>
            </div>
          </div>
        </header>
         <Nav logo={WashtaLogo} scrollToApply={scrollToApply} />
<WashtaHero/>
          

        {/* ── How it works ── */}
        <section className="how" id="how">
          <div className="wrap">
            <div className="section-head">
              <div className="section-kicker reveal">
                <span className="dot"></span>
                Stationed, not on-demand
              </div>
              <h2 className="reveal d1" style={{ fontWeight: 700 }}>
                The <span style={{ color: "#6b60e4" }}>carwash expert team</span> is already there. That's the whole point.
              </h2>
              <p className="how-sub reveal d2">No waiting. No one's driving across town to reach you. A car care provider is stationed right where you parked — you just tell them which bay.</p>
            </div>
            <div className="steps">
              <div className="step reveal d1">
                <div className="step-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 21s-7-6.3-7-11a7 7 0 0114 0c0 4.7-7 11-7 11z"></path>
                    <circle cx="12" cy="10" r="2.4"></circle>
                  </svg>
                </div>
                <span className="step-eyebrow">Step 1</span>
                <h3>Park at the mall</h3>
                <p>Pull into any participating mall and note your bay. That's all the provider needs to find your car.</p>
                <span className="stripe-note">
                  <span className="dot"></span>
                  Provider is <b>stationed on-site</b>
                </span>
              </div>
              <div className="step reveal d2">
                <div className="step-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <rect x="6" y="3" width="12" height="18" rx="3"></rect>
                    <path d="M10.5 18h3"></path>
                  </svg>
                </div>
                <span className="step-eyebrow">Step 2</span>
                <h3>Book in the app while you shop</h3>
                <p>Pick a carwash, detailing, or ceramic coating, then pay in-app with Apple Pay or card — no cash, no chasing.</p>
                <span className="stripe-note">
                  <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" style={{ color: "var(--ink)" }}>
                    <path d="M16.36 12.78c.02 2.5 2.19 3.33 2.21 3.34-.02.06-.35 1.2-1.15 2.37-.69 1.02-1.41 2.03-2.54 2.05-1.11.02-1.47-.66-2.74-.66-1.27 0-1.66.64-2.71.68-1.09.04-1.92-1.1-2.62-2.12-1.42-2.07-2.51-5.85-1.05-8.4.72-1.27 2.01-2.07 3.41-2.09 1.07-.02 2.08.72 2.74.72.65 0 1.88-.89 3.17-.76.54.02 2.06.22 3.03 1.64-.08.05-1.81 1.06-1.79 3.16M14.3 5.39c.58-.7.97-1.68.86-2.66-.83.03-1.84.55-2.44 1.25-.54.62-1.01 1.61-.88 2.56.93.07 1.88-.47 2.46-1.15"></path>
                  </svg>
                  <b>Apple Pay</b> · secured by Stripe
                </span>
              </div>
              <div className="step reveal d3">
                <div className="step-ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M12 2.5a9.5 9.5 0 100 19 9.5 9.5 0 000-19z"></path>
                    <path d="M8.5 12.5l2.5 2.5 4.5-5"></path>
                  </svg>
                </div>
                <span className="step-eyebrow">Step 3</span>
                <h3>Drive away clean</h3>
                <p>Get a notification the moment it's done. No waiting around, no queue — just get in and go.</p>
                <span className="stripe-note">
                  <span className="dot"></span>
                  Notified <b>the moment it's done</b>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ── More ways to wash ── */}
        <section className="more" id="plans">
          <div className="wrap">
            <div className="section-head">
              <div className="section-kicker reveal">
                <span className="dot"></span>
                More ways to wash
              </div>
              <h2 className="reveal d1" style={{ fontWeight: 700 }}>
                Wash on a schedule, or <span className="accent">prepay and save.</span>
              </h2>
              <p className="how-sub reveal d2">Two flexible ways to keep your car clean are on the way. Register your interest and we'll reach out the moment they go live.</p>
            </div>
            <div className="pair">
              <div className="pcard reveal d1">
                <div className="pcard-head">
                  <div className="pic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M4 21V8l8-5 8 5v13"></path>
                      <path d="M4 21h16"></path>
                      <path d="M9.5 21v-6h5v6"></path>
                      <path d="M9 9.5h.01M14.9 9.5h.01"></path>
                    </svg>
                  </div>
                  <span className="soon-badge">
                    <span className="dot" style={{ background: "#6B5FE4", animation: "none" }}></span>
                    Coming soon
                  </span>
                </div>
                <h3 style={{ fontWeight: 700 }}>Weekly washes at your building, all month.</h3>
                <p>Recurring weekly visits across one calendar month — set it once and your car gets washed every week, right where you park at home.</p>
                {/* ← React onClick instead of data-modal */}
                <button
                  type="button"
                  className="btn btn-primary req-btn"
                  onClick={() => openModal("building")}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  Request this in my building
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                    <path d="M5 12h14M13 6l6 6-6 6"></path>
                  </svg>
                </button>
              </div>
              <div className="pcard reveal d2">
                <div className="pcard-head">
                  <div className="pic">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                      <path d="M3 8l9-4 9 4-9 4-9-4z"></path>
                      <path d="M3 8v8l9 4 9-4V8"></path>
                      <path d="M7.5 10v6"></path>
                    </svg>
                  </div>
                  <span className="soon-badge">
                    <span className="dot" style={{ background: "#6B5FE4", animation: "none" }}></span>
                    Coming soon
                  </span>
                </div>
                <h3 style={{ fontWeight: 700 }}>Prepay, save, wash whenever.</h3>
                <p>Prepaid packs priced separately for Sedan and SUV. One credit = a base exterior wash; add an interior clean at checkout whenever you need it.</p>
                {/* ← React onClick instead of data-modal */}
                <button
                  type="button"
                  className="btn btn-outline req-btn"
                  onClick={() => openModal("packs")}
                >
                  Notify me when it launches
                </button>
              </div>
            </div>
            <p className="more-foot reveal">These services are launching soon — registering your interest helps us prioritise your area.</p>
          </div>
        </section>

        <footer>
          <div className="ft-wrap">
            <div className="ft-top">
              <div className="ft-brand">
                <a href="/" className="ft-logo" aria-label="Washta">
                  <img src={WashtaLogo} alt="Washta" style={{ height: "22px" }} />
                </a>
                <p className="ft-tagline">Platform only · We connect customers with stationed providers</p>
                <span className="ft-badge">
                  <span className="ft-dot" />
                  Dubai &amp; Abu Dhabi
                </span>
              </div>
              <div className="ft-right">
                <div className="ft-col">
                  <span className="ft-col-title">Contact</span>
                  <a href="mailto:info@washta.com">
                    <CiMail /> info@washta.com
                  </a>
                </div>
                <div className="ft-col">
                  <span className="ft-col-title">Legal</span>
                  <a href="/PrivacyPolicy"><IoShieldCheckmarkOutline /> Privacy Policy</a>
                  <a href="/TermsOfUse"><FaRegFileAlt /> Terms of Use</a>
                </div>
              </div>
            </div>
            <div className="ft-bottom">
              <span className="ft-copy">© 2026 Washta. All rights reserved.</span>
              <span className="ft-powered" style={{ display: "flex" }}>
                Powered by{" "}
                <a href="https://bigwals.com" target="_blank" rel="noopener noreferrer" style={{ color: "#6b5fe4" }}>
                  Bigwals
                </a>
              </span>
            </div>
          </div>
        </footer>
      </main>
    </>
  );
}